import React, { useEffect, useState, useRef } from "react";
import radarIcon from '../../images/radar.svg';
import rainIcon from '../../images/rain.svg';
import flashIcon from '../../images/flash.png';
import { URL_START } from '../../data/config';

const Radar = ({ map }) => {
  const [isVisible, setIsVisible] = useState(
    localStorage.getItem("radarVisible") === "true"
  );
  const [selectedMode, setSelectedMode] = useState("RR");
  const updateInterval = useRef(null);
  const isInitialMount = useRef(true);

  const WMS_BASE_URL =
    "https://openwms.fmi.fi/geoserver/wms?service=WMS&version=1.1.0&request=GetMap&layers=Radar:suomi_{mode}_eureffin&styles=&bbox={bbox-epsg-3857}&width=256&height=256&srs=EPSG:3857&format=image/png&transparent=true";

  const getWmsUrl = (mode) => {
    return WMS_BASE_URL.replace("{mode}", mode.toLowerCase());
  };

  const fetchLightningData = async () => {
    try {
      const response = await fetch(URL_START + "/api/lightning");

      const data = await response.json();

      // if no lightning is found
      if (data.error || !data.features) return null;

      return data;
    } catch (error) {
      console.error("Error fetching lightning data:", error);
      return null;
    }
  };

  // Moved out so can be reused in other useEffects
  const addOrUpdateRadarLayer = () => {
    if (!map) return;
    const radarSourceId = "radar-source";
    const radarLayerId = "radar-layer";
    const wmsUrl = getWmsUrl(selectedMode);
    const uniqueTime = new Date().getTime();
    const newRadarUrl = `${wmsUrl}&_=${uniqueTime}`;

    if (!map.getSource(radarSourceId)) {
      map.addSource(radarSourceId, {
        type: "raster",
        tiles: [newRadarUrl],
        tileSize: 256,
      });
    }

    if (!map.getLayer(radarLayerId)) {
      map.addLayer(
        {
          id: radarLayerId,
          type: "raster",
          source: radarSourceId,
          paint: {
            "raster-opacity": 0.3,
          },
          maxzoom: 9,
        },
        "lower-airspace"
      );
    }
  };

  useEffect(() => {
    if (!map) return;

    const radarSourceId = "radar-source";
    const radarLayerId = "radar-layer";
    const lightningSourceId = "lightning-source";
    const lightningLayerId = "lightning-layer";

    let isMounted = true;

    const addOrUpdateLightningLayer = async () => {
      if (!isMounted) return;
      const data = await fetchLightningData();
      if (!data) return;

      if (!map.getSource(lightningSourceId)) {
        map.addSource(lightningSourceId, {
          type: "geojson",
          data: data,
        });
      } else {
        map.getSource(lightningSourceId).setData(data);
      }

      map.loadImage(flashIcon, (error, image) => {
        if (error) throw error;
        if (!map.hasImage("flash-icon")) {
          map.addImage("flash-icon", image);
        }
      });

      if (!map.getLayer(lightningLayerId)) {
        map.addLayer({
          id: lightningLayerId,
          type: "symbol",
          source: lightningSourceId,
          layout: {
            "icon-image": "flash-icon",
            "icon-size": 0.12,
            "icon-allow-overlap": true,
          },
          paint: {
            "icon-opacity": 0.8,
          }
        },
        "lower-airspace"
        );
      }
    };

    const removeRadarLayer = () => {
      if (map.getLayer(radarLayerId)) map.removeLayer(radarLayerId);
      if (map.getSource(radarSourceId)) map.removeSource(radarSourceId);
    };

    const removeLightningLayer = () => {
      if (map.getLayer(lightningLayerId)) map.removeLayer(lightningLayerId);
      if (map.getSource(lightningSourceId)) map.removeSource(lightningSourceId);
      if (map.hasImage("flash-icon")) map.removeImage("flash-icon");
    };

    const updateLayers = async () => {
      if (!isMounted) return;

      const wmsUrl = getWmsUrl(selectedMode);
      const uniqueTime = new Date().getTime();
      const newRadarUrl = `${wmsUrl}&_uniqueTime=${uniqueTime}`;

      if (map.getSource(radarSourceId)) {
        map.getSource(radarSourceId).setTiles([newRadarUrl]);
      }

      const lightningData = await fetchLightningData();
      if (lightningData && map.getSource(lightningSourceId)) {
        map.getSource(lightningSourceId).setData(lightningData);
      }
    };

    const onStyleLoad = () => {
      if (isVisible) {
        addOrUpdateRadarLayer();
        addOrUpdateLightningLayer();

        if (updateInterval.current) clearInterval(updateInterval.current);
        updateInterval.current = setInterval(updateLayers, 10 * 60 * 1000);
      } else {
        removeRadarLayer();
        removeLightningLayer();
        if (updateInterval.current) {
          clearInterval(updateInterval.current);
          updateInterval.current = null;
        }
      }
    };

    map.on("load", onStyleLoad);
    map.on("styledata", onStyleLoad);

    if (map.isStyleLoaded()) {
      onStyleLoad();
    }

    return () => {
      isMounted = false;
      removeRadarLayer();
      removeLightningLayer();
      if (updateInterval.current) {
        clearInterval(updateInterval.current);
        updateInterval.current = null;
      }
      map.off("load", onStyleLoad);
      map.off("styledata", onStyleLoad);
    };
  }, [map, isVisible, selectedMode]);

  // New effect: update radar tiles immediately when selectedMode changes & radar visible
  useEffect(() => {
    if (!map || !isVisible) return;

    const radarSourceId = "radar-source";
    if (map.getSource(radarSourceId)) {
      const wmsUrl = getWmsUrl(selectedMode);
      const uniqueTime = new Date().getTime();
      const newRadarUrl = `${wmsUrl}&_uniqueTime=${uniqueTime}`;
      map.getSource(radarSourceId).setTiles([newRadarUrl]);
    } else {
      addOrUpdateRadarLayer();
    }
  }, [selectedMode, isVisible, map]);

  const toggleVisibility = () => {
    const newVisibility = !isVisible;
    setIsVisible(newVisibility);
    localStorage.setItem("radarVisible", newVisibility.toString());
  };

  const handleModeChange = (mode) => {
    setSelectedMode(mode);
  };

  return (
    <div>
      <button
        data-tooltip-id="map-buttons-tooltip"
        data-tooltip-content="WX Radar & Lightning"
        className={`w-8 h-8 ${
          isVisible ? "bg-gray-600" : "bg-gray-400"
        } text-white flex items-center justify-center`}
        onClick={toggleVisibility}
      >
        <img className="w-6" src={radarIcon} alt="Radar" />
      </button>
      {isVisible && (
        <div
          className="flex space-x-2 mt-2 text-white text-xs"
          style={{ position: "absolute", bottom: "0", left: "0" }}
        >
          {["RR", "RR1h", "RR12h", "RR24h"].map((mode) => (
            <button
              key={mode}
              className={`p-1.5 flex w-14 items-center ${
                selectedMode === mode ? "bg-gray-600" : "bg-gray-400"
              }`}
              onClick={() => handleModeChange(mode)}
            >
              <img className="w-5" src={rainIcon} alt={mode} />
              <span className="ml-1">{mode === "RR" ? "RR" : mode.replace("RR", "")}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Radar;
